import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  type GroupInput,
  type PortalGroup,
} from '@internal/backstage-plugin-portalgroups-common';
import { PortalgroupsApi } from './PortalgroupsApi';

export class PortalgroupsClient implements PortalgroupsApi {
  readonly #discoveryApi: DiscoveryApi;
  readonly #fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.#discoveryApi = options.discoveryApi;
    this.#fetchApi = options.fetchApi;
  }

  private async baseUrl(): Promise<string> {
    return this.#discoveryApi.getBaseUrl('portalgroups');
  }

  /** Extracts a readable message from a Backstage error response body. */
  private extractErrorMessage(body: any, fallback: string): string {
    if (typeof body.error === 'string') return body.error;
    if (typeof body.error?.message === 'string') return body.error.message;
    return fallback;
  }

  async fetchMyGroups(): Promise<PortalGroup[]> {
    const resp = await this.#fetchApi.fetch(
      `${await this.baseUrl()}/my-groups`,
    );
    if (!resp.ok) {
      throw new Error(`Failed to fetch groups: ${resp.statusText}`);
    }
    return resp.json();
  }

  async createGroup(input: GroupInput): Promise<PortalGroup> {
    const resp = await this.#fetchApi.fetch(`${await this.baseUrl()}/group`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      throw new Error(
        this.extractErrorMessage(
          body,
          `Failed to create group: ${resp.statusText}`,
        ),
      );
    }
    return resp.json();
  }

  async updateGroup(input: GroupInput): Promise<PortalGroup> {
    const resp = await this.#fetchApi.fetch(`${await this.baseUrl()}/group`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      throw new Error(
        this.extractErrorMessage(
          body,
          `Failed to update group: ${resp.statusText}`,
        ),
      );
    }
    return resp.json();
  }
}
